import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSet } from 'react-use';
import { useDebounce } from 'use-debounce';
import { useIsEmbeddedMobile } from '../../../_hooks/useIsEmbeddedMobile';
import { usePerformanceDetails } from '../../hooks/usePerformanceDetails';
import { usePeriod } from '../../hooks/usePeriod';
import { useRouteParams } from '../../hooks/useRouteParams';
import { setGroupSelectionSelectedTabIndex } from '../../state/actions';
import { selectGroupSelectionData } from '../../state/selectors';
import { Sorting } from '../../tabs/Positions/components/AlternativeDetail/AlternativeDetail.meta';
import { TabSlug } from '../../types';
import { PerformanceDetailsEmpty } from './PerformanceDetails.empty';
import { PerformanceDetailsError } from './PerformanceDetails.error';
import { PerformanceDetailsLoading } from './PerformanceDetails.loading';
import { PerformanceDetailsContent } from './components/PerformanceDetailsContent/PerformanceDetailsContent';

interface Props {
  title: string;
}

export const PerformanceDetails = ({ title }: Props) => {
  const dispatch = useDispatch();

  const { accountId, clientId, groupId } = useRouteParams();

  const [, { has: isRowExpanded, toggle: toggleRow, reset }] = useSet<string>();

  const { selectedTabIndex, tabList } = useSelector(selectGroupSelectionData(TabSlug.Performance));

  const setSelectedTabIndex = (index: number) =>
    dispatch(setGroupSelectionSelectedTabIndex(index, TabSlug.Performance));

  const period = usePeriod();

  const isEmbeddedMobile = useIsEmbeddedMobile();

  const { isFetching, isError, refetch, data } = usePerformanceDetails({
    clientId,
    period: period.value,
    startDate: period.dates[0],
    endDate: period.dates[1],
    accountId,
    groupId,
    isEmbeddedMobile,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);

  const [sorting, setSorting] = useState<Sorting>({});

  // Reset the set when changing tabs or fetching a new range
  useEffect(() => {
    reset();
  }, [reset, selectedTabIndex, isFetching]);

  useEffect(() => {
    setSearchTerm('');
  }, [selectedTabIndex]);

  if (isFetching) {
    return <PerformanceDetailsLoading />;
  }

  if (isError) {
    return <PerformanceDetailsError onRetry={refetch} />;
  }

  if (!data) {
    return null;
  }

  if (!tabList) {
    return <PerformanceDetailsEmpty />;
  }

  return (
    <PerformanceDetailsContent
      debouncedSearchTerm={debouncedSearchTerm}
      data={data}
      isRowExpanded={isRowExpanded}
      searchTerm={searchTerm}
      selectedTabIndex={selectedTabIndex}
      setSearchTerm={setSearchTerm}
      setSelectedTabIndex={setSelectedTabIndex}
      setSorting={setSorting}
      sorting={sorting}
      toggleRow={toggleRow}
      title={title}
    />
  );
};
